<template>
    <div class="club club-settings">
        <div v-if="venue" class="content">
            <div class="gallery-container">
                <GalleryImages :image-urls="venue.imageUrls" @save="saveImages" />
                <div class="gallery">
                    <div class="img-container">
                        <div class="img" :style="`background-image: url(${venue.imageUrls[0]}`"></div>
                    </div>
                    <div class="img-container">
                        <div class="img" :style="`background-image: url(${venue.imageUrls[1]}`"></div>
                        <div class="img" :style="`background-image: url(${venue.imageUrls[2]}`"></div>
                    </div>
                    <div class="img-container">
                        <div class="img" :style="`background-image: url(${venue.imageUrls[3]}`"></div>
                    </div>
                </div>
            </div>
            <div class="main-content">
                <div class="profile-container">
                    <div v-if="venue" class="profile-img" :style="`background-image: url(${venue.logoUrl}`">
                        <Button
                            class="edit-profile-img"
                            icon="camera"
                            type="white"
                            :value="$t('general.edit')"
                            @click="openProfileImageUpload()"
                        />
                    </div>
                    <div class="name-container">
                        <div class="name">
                            <h1>{{ venue.name }}</h1>
                            <div class="copy-s">@{{ venue.name.replace(/\s+/g, '_').toLowerCase() }}</div>
                        </div>
                        <div class="edit copy-s bold" @click="modalOpen = 'name'">{{ $t('general.edit') }}</div>
                    </div>
                    <CapacityCard
                        :current="34"
                        :max="venue.capacity"
                        :show-add-user="false"
                        :venue-i-d="venue.id"
                        :show-venue="false"
                    />
                    <QRCodeCard type="Venue" @click="openQRCode()" />
                    <div class="genre-container">
                        <div class="genre-headline">
                            <div class="label">{{ $t('venue_settings.genre_container.headline') }}</div>
                            <div class="edit copy-s bold" @click="modalOpen = 'genres'">{{ $t('general.edit') }}</div>
                        </div>
                        <div class="genres">
                            <Tag v-for="genre in venue.genres" :key="genre" :value="genre" />
                        </div>
                    </div>
                </div>
                <div class="settings-container">
                    <FacebookSyncSetting />
                    <div class="settings-group">
                        <SettingsManager
                            v-if="!$apollo.queries.venue.loading"
                            :settings="settings"
                            type="venue"
                            :apollo-data="$apolloData.data.venue"
                            @save="saveSetting"
                        />
                    </div>
                    <div class="settings-group">
                        <SettingsManager
                            v-if="!$apollo.queries.venue.loading"
                            :settings="moreSettings"
                            type="venue"
                            :apollo-data="$apolloData.data.venue"
                            @save="saveSetting"
                            @reload="$apollo.queries.venue.refetch()"
                        />
                        <Button
                            icon="download"
                            class="download-check-in-data"
                            type="secondary"
                            :value="$t('venue.download_check_in_data')"
                            @click="downloadCheckInData"
                        />
                    </div>
                </div>
            </div>
        </div>
        <SettingsModal
            v-if="modalOpen !== false"
            :setting="activeSetting"
            :pre-value="$apolloData.data.venue[modalOpen].toString()"
            @close="closeSetting"
            @save="saveSetting"
        />
        <Modal v-if="profileImageUploadOpen" class="modal-profile-image" @cancel="closeProfileImageUpload()">
            <div class="modal-content">
                <div class="title-container">
                    <div class="title copy-l bold">{{ $t('venue_settings.venue_img_modal.headline') }}</div>
                    <div class="copy-s">{{ $t('venue_settings.venue_img_modal.description') }}</div>
                </div>
                <ImageUpload hide-delete @image="(e) => handleImage(e)" />
                <div class="action-container fullwidth">
                    <Button :value="$t('general.cancel')" type="secondary" @click="closeProfileImageUpload()" />
                    <Button :value="$t('general.save')" type="primary" @click="saveImage()" />
                </div>
            </div>
        </Modal>
    </div>
</template>

<script>
import Button from '@/components/Button'
import Tag from '@/components/Tag'
import FacebookSyncSetting from './FacebookSyncSetting'
import gql from 'graphql-tag'
import defaultSettings from '../../models/venue.json'
import SettingsManager from '@/components/SettingsManager'
import SettingsModal from '@/components/SettingsModal'
import CapacityCard from '@/components/CapacityCard'
import QRCodeCard from '@/components/QRCodeCard'
import Modal from '@/components/Modal'
import ImageUpload from '@/components/ImageUpload'
import GalleryImages from '@/components/GalleryImages'

export default {
    name: 'VenueSettings',
    components: {
        Button,
        Tag,
        FacebookSyncSetting,
        SettingsManager,
        SettingsModal,
        CapacityCard,
        QRCodeCard,
        Modal,
        ImageUpload,
        GalleryImages,
    },
    data() {
        return {
            modalOpen: false,
            diffSettings: [
                {
                    name: 'name',
                    type: 'text',
                    value: '',
                    description: 'Event Titel',
                    empty: 'Event Titel',
                    icon: '',
                },
                {
                    name: 'genres',
                    type: 'tags',
                    value: '',
                    description: 'Genres hinzufügen',
                    empty: 'Genres',
                    icon: 'tag',
                    selectOptions: [
                        { value: 'AlternativeIndie', text: 'AlternativeIndie' },
                        { value: 'Country', text: 'Country' },
                        { value: 'Dance', text: 'Dance' },
                        { value: 'EDM', text: 'EDM' },
                        { value: 'Electro', text: 'Electro' },
                        { value: 'Goa', text: 'Goa' },
                        { value: 'HipHop', text: 'HipHop' },
                        { value: 'House', text: 'House' },
                        { value: 'Jazz', text: 'Jazz' },
                        { value: 'Latin', text: 'Latin' },
                        { value: 'Metal', text: 'Metal' },
                        { value: 'Pop', text: 'Pop' },
                        { value: 'Rock', text: 'Rock' },
                        { value: 'Techno', text: 'Techno' },
                    ],
                },
            ],
            club: null,
            profileImageUploadOpen: false,
            newsrc: '',
        }
    },
    computed: {
        settings() {
            return defaultSettings.settings
        },
        moreSettings() {
            return [
                {
                    name: 'capacity',
                    type: 'number',
                    value: this.$apolloData.data.venue.capacity,
                    icon: 'capacity',
                },
                {
                    name: 'checkoutTimer',
                    type: 'number',
                    value: this.$apolloData.data.venue.checkoutTimer,
                    icon: 'capacity',
                },
                {
                    name: 'capacityManagement',
                    type: 'boolean',
                    value: this.$apolloData.data.venue.capacityManagement,
                    icon: '',
                },
                {
                    name: 'covidDataRequired',
                    type: 'boolean',
                    value: this.$apolloData.data.venue.covidDataRequired,
                    icon: '',
                },
            ]
        },
        activeSetting() {
            return this.diffSettings.find((x) => x.name === this.modalOpen)
        },
    },
    apollo: {
        venue: {
            query: gql`
                query ($id: ID!) {
                    venue(id: $id) {
                        id
                        email
                        phone
                        websiteUrl
                        dresscode
                        name
                        logoUrl
                        genres
                        priceLevel
                        capacity
                        imageUrls
                        checkinQRCode
                        capacityManagement
                        covidDataRequired
                        checkoutTimer
                    }
                }
            `,
            variables() {
                return {
                    id: this.$route.params.id,
                }
            },
        },
    },
    methods: {
        async saveImages(images) {
            if (this.isDraft) {
                this.event.imageUrls = images
                return
            }

            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation ($id: ID!, $value: PartialVenueInput!) {
                            updateVenue(id: $id, input: $value) {
                                imageUrls
                            }
                        }
                    `,
                    variables: {
                        id: this.venue.id,
                        value: { imageUrls: images },
                    },
                })
                .then((data) => {
                    this.venue.imageUrls = data.data.updateVenue.imageUrls
                })
                .catch((err) => {
                    console.error(err)
                    this.$pm.createToast('error', this.$t('errors.img_could_not_be_saved'))
                })
        },
        openProfileImageUpload() {
            this.profileImageUploadOpen = true
        },
        closeProfileImageUpload() {
            this.profileImageUploadOpen = false
        },
        handleImage(src) {
            this.newsrc = src
        },
        async saveImage() {
            if (this.isDraft) {
                this.event.coverImageUrl = this.newsrc
                return
            }
            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation ($id: ID!, $value: PartialVenueInput!) {
                            updateVenue(id: $id, input: $value) {
                                logoUrl
                            }
                        }
                    `,
                    variables: {
                        id: this.venue.id,
                        value: { logoUrl: this.newsrc },
                    },
                })
                .then((data) => {
                    this.venue.logoUrl = data.data.updateVenue.logoUrl
                })
                .catch((err) => {
                    console.error(err)
                    this.$pm.createToast('error', this.$t('errors.img_could_not_be_saved'))
                })
            this.closeProfileImageUpload()
        },
        closeSetting() {
            this.modalOpen = false
        },
        async openQRCode() {
            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation ($id: ID!) {
                            generateVenueCheckinPDF(venueID: $id, webCheckinFallback: true)
                        }
                    `,
                    variables: {
                        id: this.venue.id,
                    },
                })
                .then((res) => {
                    this.downloadURI(res.data.generateVenueCheckinPDF, `${this.venue.name} Check-in QR Code.pdf`)
                })
                .catch(() => {
                    this.$pm.createToast('error', this.$t('errors.qr_code_could_not_be_opened'))
                })
        },
        downloadURI(uri, name) {
            let link = document.createElement('a')
            link.download = name
            link.href = uri
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },
        async downloadCheckInData() {
            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation ($endDate: Date!, $startDate: Date!, $id: ID!) {
                            downloadCoronaCheckins(endDate: $endDate, startDate: $startDate, venueID: $id)
                        }
                    `,
                    variables: {
                        id: this.venue.id,
                        endDate: new Date(),
                        startDate: new Date().setDate(new Date().getDate() - 30),
                    },
                })
                .then((res) => {
                    this.downloadURI(res.data.downloadCoronaCheckins, `${this.venue.name} Check-in Data.xlsx`)
                })
                .catch(() => {
                    this.$pm.createToast('error', this.$t('errors.qr_code_could_not_be_opened'))
                })
        },
        async saveSetting(setting) {
            this.modalOpen = false
            let name = setting.name
            let venueInput = {}
            venueInput[name] = setting.value
            this.venue[name] = setting.value

            let venueId = this.$route.params.id

            await this.$apollo
                .mutate({
                    mutation: gql`mutation($id: ID!, $value: PartialVenueInput!) {
                  updateVenue(id: $id, input: $value) {
                    ${name}
                  }
                }
                `,
                    variables: {
                        id: venueId,
                        value: venueInput,
                    },
                })
                .catch(() => {
                    this.$pm.createToast('error', this.$t('errors.settings_could_not_be_saved'))
                })
        },
    },
}
</script>

<style scoped lang="sass">
.club-settings
    .gallery-container
        position: relative

        ::v-deep .button.edit-gallery
            position: absolute
            bottom: $m
            right: $m

        .gallery
            display: flex
            height: 260px

            .img-container + .img-container
                margin-left: $s

            .img-container
                &:nth-child(odd)
                    flex: 3

                &:nth-child(2)
                    flex: 1
                    display: flex
                    flex-direction: column

                .img + .img
                    margin-top: $s

                .img
                    height: 100%
                    background: $neutral-2
                        size: cover
                        position: center
                    border-radius: 12px

    .main-content
        margin-top: $xxl
        display: flex

        .profile-container
            flex: 1

            .profile-img
                width: 140px
                height: 140px
                background:
                    size: cover
                    position: center
                    color: $gray-1
                border-radius: 12px
                position: relative


                .button.edit-profile-img
                    position: absolute
                    left: $xs
                    bottom: $xs
                    right: $xs
                    transform: scale(0.95)
                    opacity: 0

                &:hover
                    .button
                        transform: scale(1)
                        opacity: 1

            .edit
                color: $neutral-2
                cursor: pointer

            .name-container
                margin-top: $m
                margin-bottom: $xl
                display: flex
                justify-content: space-between

                .edit
                    margin-top: 1.2rem

                .copy-s
                    color: $neutral-2

            .capacity-card,
            .qr-code-card
                margin-bottom: $l

            .genre-container
                .genre-headline
                    display: flex
                    justify-content: space-between
                    margin-bottom: $s

                .genres
                    .tag
                        margin-left: 0
                        margin-bottom: $xs
                        margin-right: $xs

        .settings-container
            width: 520px
            margin-left: $xl

            .download-check-in-data
                width: 100%
                margin-top: $s
                box-sizing: border-box

            .facebook-sync-setting
                margin-bottom: $l

    .modal-profile-image
        .modal-content
            .title-container
                display: block
                margin-bottom: $xl

                .copy-s
                    margin-top: $xxs

            .action-container
                margin-top: $xl
//
</style>
